export const MONTHS = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
]

/** Calendar dates and deadlines use the service's Moscow business day. */
export function plannedDate(value: string | number): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Moscow', year: 'numeric', month: '2-digit', day: '2-digit' }).format(date)
}

export function plannedDateTimestamp(value: string): string {
  const date = new Date(`${value}T00:00:00+03:00`)
  return Number.isNaN(date.getTime()) ? '' : date.toISOString()
}

export function formatDate(value?: string | number | null, withTime = false): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat('ru-RU', withTime
    ? { timeZone: 'Europe/Moscow', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }
    : { timeZone: 'Europe/Moscow', day: '2-digit', month: 'short', year: 'numeric' }).format(date)
}

export function formatTime(value?: string | number | null): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat('ru-RU', { timeZone: 'Europe/Moscow', hour: '2-digit', minute: '2-digit' }).format(date)
}

export function formatRelativeTime(value?: number | string | null): string {
  if (!value) return 'ещё не было'
  const timestamp = typeof value === 'number' ? value : new Date(value).getTime()
  const seconds = Math.round((timestamp - Date.now()) / 1000)
  const formatter = new Intl.RelativeTimeFormat('ru', { numeric: 'auto' })
  if (Math.abs(seconds) < 60) return formatter.format(seconds, 'second')
  const minutes = Math.round(seconds / 60)
  if (Math.abs(minutes) < 60) return formatter.format(minutes, 'minute')
  const hours = Math.round(minutes / 60)
  if (Math.abs(hours) < 24) return formatter.format(hours, 'hour')
  return formatter.format(Math.round(hours / 24), 'day')
}

export function formatNumber(value?: number | string | null, maximumFractionDigits = 2): string {
  if (value === null || value === undefined || value === '') return '—'
  const parsed = typeof value === 'number' ? value : Number(String(value).replace(',', '.'))
  if (!Number.isFinite(parsed)) return String(value)
  return new Intl.NumberFormat('ru-RU', { maximumFractionDigits }).format(parsed)
}

export function formatBytes(value?: number | null): string {
  if (!value) return '—'
  if (value < 1024) return `${value} Б`
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(0)} КБ`
  return `${(value / 1024 / 1024).toFixed(1)} МБ`
}

/** Период ГГГГ-ММ → «Август 2026». */
export function periodTitle(year: number, month: number): string {
  return `${MONTHS[month - 1] || ''} ${year}`.trim()
}

export function currentPeriod(): { year: number; month: number } {
  const now = new Date()
  return { year: now.getFullYear(), month: now.getMonth() + 1 }
}

export function fileName(document: { fileName?: string; file?: { originalName?: string; name?: string } | null }): string {
  return document.fileName || document.file?.originalName || document.file?.name || 'Файл ППР'
}

/** Вид документа ППР: годовой график или месячный график с отметками по дням. */
export function documentKindLabel(document: { kind?: string | null; month?: number | null; year?: number | null }): string {
  if (document.kind === 'MONTHLY') return document.month ? `Месячный график · ${MONTHS[document.month - 1]?.toLocaleLowerCase('ru') ?? document.month} ${document.year ?? ''}`.trim() : 'Месячный график'
  return document.year ? `Годовой ППР ${document.year}` : 'Годовой ППР'
}

export function importStatusLabel(status?: string): string {
  const labels: Record<string, string> = {
    UPLOADING: 'Загружается', QUEUED: 'В очереди', PROCESSING: 'Распознаётся',
    REVIEW_REQUIRED: 'Нужна проверка', READY_TO_CONFIRM: 'Готов к подтверждению',
    CONFIRMED: 'Подтверждён', ARCHIVED: 'Архив', FAILED: 'Ошибка загрузки',
  }
  return labels[status || ''] || status || 'Статус не указан'
}

export function issueStatusLabel(status?: string): string {
  return ({ OPEN: 'Открыта', RESOLVED: 'Исправлена', IGNORED: 'Пропущена' } as Record<string, string>)[status || ''] || status || '—'
}

export function equipmentStatusLabel(status?: string): string {
  return ({ ACTIVE: 'В работе', INACTIVE: 'Неактивно', ARCHIVED: 'В архиве' } as Record<string, string>)[status || ''] || status || 'Не указан'
}

export function componentStatusLabel(status?: string): string {
  return ({ ACTIVE: 'В работе', REPAIR: 'Требует внимания', INACTIVE: 'Неактивен' } as Record<string, string>)[status || ''] || status || 'Не указан'
}

export function planStatusLabel(status?: string): string {
  return ({ DRAFT: 'Черновик', GENERATED: 'Сформирован', APPROVED: 'Утверждён' } as Record<string, string>)[status || ''] || status || 'Сформирован'
}

export function planItemStatusLabel(status?: string): string {
  return ({ CANDIDATE: 'Без задания', SCHEDULED: 'Задание создано', CANCELLED: 'Отменена' } as Record<string, string>)[status || ''] || status || '—'
}

export const WORK_ORDER_STATUS_LABELS: Record<string, string> = {
  PLANNED: 'Запланировано', ASSIGNED: 'Назначено', IN_PROGRESS: 'В работе', COMPLETED: 'Выполнено',
  REJECTED: 'На доработке', CANCELLED: 'Отменено', OVERDUE: 'Просрочено',
}

export function workOrderStatusLabel(status?: string): string {
  return WORK_ORDER_STATUS_LABELS[status || ''] || status || '—'
}

export function workOrderStatusTone(status?: string): 'neutral' | 'success' | 'warning' | 'danger' | 'info' {
  switch (status) {
    case 'COMPLETED': return 'success'
    case 'IN_PROGRESS': return 'info'
    case 'OVERDUE': case 'REJECTED': return 'danger'
    case 'CANCELLED': return 'neutral'
    default: return 'warning'
  }
}

export const DEFECT_STATUS_LABELS: Record<string, string> = {
  OPEN: 'Открыт', IN_PROGRESS: 'В работе', RESOLVED: 'Устранён', CLOSED: 'Закрыт', CANCELLED: 'Отменён',
}

export function defectStatusLabel(status?: string): string {
  return DEFECT_STATUS_LABELS[status || ''] || status || '—'
}

export function defectStatusTone(status?: string): 'neutral' | 'success' | 'warning' | 'danger' | 'info' {
  switch (status) {
    case 'CLOSED': return 'success'
    case 'RESOLVED': return 'info'
    case 'IN_PROGRESS': return 'info'
    case 'CANCELLED': return 'neutral'
    default: return 'warning'
  }
}

export const DEFECT_PRIORITY_LABELS: Record<string, string> = { LOW: 'Низкий', NORMAL: 'Обычный', HIGH: 'Высокий', CRITICAL: 'Критичный' }

export function defectPriorityLabel(priority?: string): string {
  return DEFECT_PRIORITY_LABELS[priority || ''] || priority || '—'
}

export function defectPriorityTone(priority?: string): 'neutral' | 'success' | 'warning' | 'danger' | 'info' {
  return priority === 'CRITICAL' ? 'danger' : priority === 'HIGH' ? 'warning' : priority === 'LOW' ? 'neutral' : 'info'
}

export const RESULT_TYPE_LABELS: Record<string, string> = {
  CHECK: 'Выполнено / не выполнено', PASS_FAIL: 'Норма / не норма', TEXT: 'Текст', NUMBER: 'Число', MEASUREMENT: 'Измерение',
  PHOTO: 'Фотография', DATE: 'Дата', SELECT: 'Один вариант', MULTISELECT: 'Несколько вариантов',
}

export function resultTypeLabel(type?: string): string {
  return RESULT_TYPE_LABELS[type || ''] || type || '—'
}

export const DEFECT_RULE_LABELS: Record<string, string> = { NONE: 'Дефект не предлагать', ON_FAIL: 'При «не норма» предлагать дефект', REQUIRE: 'При «не норма» дефект обязателен' }

export function defectRuleLabel(rule?: string): string {
  return DEFECT_RULE_LABELS[rule || ''] || rule || '—'
}

export function answerLabel(answer?: string | null, resultType?: string): string {
  if (!answer) return 'Нет ответа'
  if (answer === 'SKIPPED') return 'Пропущено'
  if (resultType === 'CHECK') return answer === 'NORMAL' ? 'Выполнено' : 'Не выполнено'
  return answer === 'NORMAL' ? 'Норма' : 'Не норма'
}

export const REPORT_STATUS_LABELS: Record<string, string> = { DRAFT: 'Черновик', APPROVED: 'Утверждён', SUPERSEDED: 'Заменён новой версией' }

export function reportStatusLabel(status?: string): string {
  return REPORT_STATUS_LABELS[status || ''] || status || '—'
}

export function aiStatusLabel(status?: string): string {
  return ({ NONE: 'Не выполнялась', QUEUED: 'В очереди', RUNNING: 'Выполняется', DONE: 'Выполнена', PARTIAL: 'Выполнена частично', FAILED: 'Ошибка', SKIPPED: 'Пропущена' } as Record<string, string>)[status || ''] || status || '—'
}

export function severityLabel(severity?: string): string {
  return ({ BLOCKER: 'Блокирует', WARNING: 'Внимание', INFO: 'К сведению' } as Record<string, string>)[severity || ''] || severity || '—'
}

export function severityTone(severity?: string): 'neutral' | 'success' | 'warning' | 'danger' | 'info' {
  return severity === 'BLOCKER' ? 'danger' : severity === 'WARNING' ? 'warning' : 'info'
}

export function roleLabel(roles: string[]): string {
  if (roles.includes('ROLE_ADMIN')) return 'Администратор'
  if (roles.includes('ROLE_ENGINEER')) return 'Инженер'
  if (roles.includes('ROLE_SUPERVISOR')) return 'Руководитель'
  if (roles.includes('ROLE_MANAGER')) return 'Менеджер'
  if (roles.includes('ROLE_EXECUTOR')) return 'Исполнитель'
  return 'Пользователь'
}

export function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Не удалось выполнить действие'
}
